import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Card, CardBody, CardTitle, CardText, DashboardSection, Header1 } from 'fiducius-ui';
import { NeedAssistance } from '../../need-assistance';
import Scheduler from '../../meetings/containers/scheduler';
import { withPermissions } from '../../routing';
import FaqQuestion from '../../faq/components/faq-question';
import { isStringWithContent } from '../../utils';

const RedSpan = styled.span`
  color: red;
`;

const ContactNeedAssistance = ({ permissions, needAssistanceObject }) => {
  const needAssistanceText =
    needAssistanceObject.length &&
    needAssistanceObject.find((element) => {
      return element.id === 'MIK12_Assistance';
    }).messageContent;
  const needAssistanceAvatarLink =
    needAssistanceObject.length &&
    needAssistanceObject.find((element) => {
      return element.id === 'MIK12_Assistance';
    }).avatarLink;
  const blocktext =
    isStringWithContent(needAssistanceText) || isStringWithContent(needAssistanceAvatarLink)
      ? 'block'
      : 'none';

  return (
    <div className="row">
      <div className="col-12 col-lg-12">
        <DashboardSection>
          <div className="row">
            <div className="col-12 col-lg-9">
              <Header1>Need Assistance?</Header1>
            </div>
            <>
              <div className="col-12 col-lg-3" style={{ 'text-align': 'right' }}>
                <Scheduler buttonText="Speak to an Expert" />
              </div>
            </>
          </div>

          <Card style={{ display: blocktext }}>
            <CardBody>
              <CardTitle>Information</CardTitle>
              <CardText>
                <NeedAssistance messageType={'MIK12_Assistance'} />
              </CardText>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <CardTitle>FAQs</CardTitle>
              <FaqQuestion
                question={
                  'Does my district apply on my behalf or do individuals apply on their own?'
                }
              >
                <p>
                  The Michigan 27k student loan repayment program requires individuals to apply
                  directly for consideration at{' '}
                  <a
                    href="https://mdoe.state.mi.us/GEMS/Public/LoanRepayment27kIndividual.aspx"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://mdoe.state.mi.us/GEMS/Public/LoanRepayment27kIndividual.aspx
                  </a>
                  . This is a major difference from Cohort 1, where your district submitted the
                  application on your behalf. If you do not submit a valid application by 5/19/2025
                  at 5 p.m. you will not be receiving money from the state.
                </p>
              </FaqQuestion>
              <FaqQuestion question={'What do I need to have prior to submitting my application?'}>
                <p>
                  You will need to provide:
                  <ul>
                    <li>Relevant personal information</li>
                    <ul>
                      <li>Email</li>
                      <li>Secondary Email</li>
                      <li>PIC number</li>
                      <li>Date of Birth</li>
                      <li>District Name & Code (Searchable Database)</li>
                    </ul>
                    <li>Repayment Proof Documentation</li>
                    <ul>
                      <li>See the video links above on how to obtain.</li>
                      <li>Proof of each month’s payment will be required</li>
                    </ul>
                    <li>Exact Monthly Payment Amounts</li>
                    <ul>
                      <li>October 2024 – September 2025</li>
                    </ul>
                    <li>PSLF Qualifying Payment Documentation</li>
                    <ul>
                      <li>See the video links above on how to obtain.</li>
                      <li>Proof of what loan</li>
                    </ul>
                  </ul>
                </p>
                <p>
                  <b>
                    <u>
                      It is vital you collect this information prior to starting the application.
                      Once you begin your application, you only have 30 minutes to complete.
                    </u>
                  </b>
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={'What are the eligible PSLF loan types for 27k consideration?'}
              >
                <p>
                  Eligible loan types include:
                  <ul>
                    <li>Direct Subsidized Loans</li>
                    <li>Direct Unsubsidized Loans</li>
                    <li>Direct PLUS loans</li>
                    <li>Direct Consolidation Loans</li>
                  </ul>
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={'What are eligible repayment plan types for 27k consideration?'}
              >
                <p>
                  Eligible loan types include:
                  <ul>
                    <li>Income-Based Repayment (IBR)</li>
                    <li>Pay As You Earn (PAYE)</li>
                    <li>Income-Contingent Repayment (ICR)</li>
                    <li>
                      10 yr Standard repayment plan (if not eligible for any of the other plans
                      listed)
                    </li>
                    <li>Saving on a Valuable Education (SAVE)</li>
                  </ul>
                </p>
                <p>
                  Note: In order to receive money in the 27k program, you must actively make
                  payments towards one of these plans while in the “repayment” status. You will not
                  receive money from the state if you make a payment towards one of these plans
                  while in a “forbearance or deferment” status.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'Why won’t I receive money while on the SAVE repayment plan, even if I am eligible?'
                }
              >
                <p>
                  The SAVE repayment plan has been blocked by the Supreme Court and consequentially
                  an administrative forbearance has been placed on any account with the SAVE plan in
                  place.
                </p>
                <p>
                  Administrative forbearance is not an eligible status and thus will result in zero
                  dollars received. It is highly recommended you utilize the Fiducius platform to
                  switch off of the SAVE plan to unlock 27k funding. Follow your To-Do list to move
                  off of the SAVE plan.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={'How can I confirm that my application was submitted successfully?'}
              >
                <p>
                  When you submit your application, the system will show you a message that your
                  application has been submitted. It is recommended that you save a screenshot of
                  this message for your records and download the copy of your application using the
                  link provided on the submission message screen. In addition, you will receive an
                  automated e-mail confirming submission to the e-mail address you provided in your
                  application.
                </p>
                <p>
                  <RedSpan>
                    <b>
                      <u>IMPORTANT:</u>
                    </b>
                  </RedSpan>{' '}
                  To mitigate potential technical issues with the application system, DO NOT WAIT
                  until the end of the application window to apply. We are expecting heavy volumes
                  of applications within the last 48 hours! Please be advised that there are{' '}
                  <RedSpan>
                    <b>
                      <u>no exceptions</u>
                    </b>
                  </RedSpan>{' '}
                  allowed for applications that are submitted after the{' '}
                  <RedSpan>
                    <b>
                      <u>deadline of 5:00 PM, Monday, May 19, 2025.</u>
                    </b>
                  </RedSpan>
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'What do I enter in the payment fields (October – April) if I do not make a payment, have a payment due or in forbearance/deferment status?'
                }
              >
                <p>
                  Per State guidelines, you should enter $0.00 for any month in which a payment was
                  not made, including forbearance and/or deferment.
                </p>
                <p>
                  Remember, for any amount you enter greater than $0.00, you will be required to
                  submit payment proof or else your application will be denied.
                </p>
              </FaqQuestion>
              <FaqQuestion question={'What do I enter in the payment fields (May - September)?'}>
                <p>
                  You should enter the current repayment amount in place. Remember, you are required
                  to update the MDE and your district any time your payment changes.
                </p>
                <p>If no payment is currently do, you should enter $0.</p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'If I am in the federal student loan forgiveness program, but do not currently have a payment due, should I submit an FY25 application if I think my payment may increase later?'
                }
              >
                <p>
                  Yes, payments under the PSLF program change throughout a given year and your new
                  payment may be higher than $0.00. You should submit an application if you meet all
                  eligibility criteria. On the application, you would enter a loan payment amount of
                  $0 for any month(s) in deferment or forbearance status.
                </p>
                <p>
                  If and when the payment increases, you will be responsible for reporting this to
                  your district and MDE.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={'What time period is included for consideration on this application?'}
              >
                <p>
                  You may report payment information starting October of 2024 through September of
                  2025. Any payments outside this period will not be considered for this cohort.
                </p>
              </FaqQuestion>
              <FaqQuestion question={'How can I find my Personnel Identification Code (PIC)?'}>
                <p>
                  You may utilize the following site to find your PIC code.
                  <br />
                  <a
                    href="https://mdoe.state.mi.us/MOECS/PublicCredentialSearch.aspx"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://mdoe.state.mi.us/MOECS/PublicCredentialSearch.aspx
                  </a>
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'If I find out later that I have made an error to my application, can I go back and change it?'
                }
              >
                <p>
                  No, the MDE is only allowing a single entry per employee. If you submit an
                  application with an error, it will be denied.
                </p>
                <p>Additionally, submitting multiple entries will result in a denial</p>
                <p>
                  It is imperative you submit one, accurate entry. If you are unsure of what to do
                  on the application, be sure to review our How to videos.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={'Will I receive payments from MDE if my application is accepted?'}
              >
                <p>You will receive the funds from your district, not the MDE or Fiducius.</p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'Who makes the final determination on whether an educator’s application is accepted (based on the listed requirements)?'
                }
              >
                <p>
                  The final determination on eligibility for the Michigan 27k program is a two-step
                  process. MDE staff are responsible for verifying loan documentation submitted by
                  individual applicants.
                </p>
                <p>
                  The individual's employing district is responsible for verifying all employment
                  data submitted with the application.
                </p>
              </FaqQuestion>
              <FaqQuestion question={'When will I receive funding?'}>
                <p>
                  There is no definitive date or deadline as to when each district will receive
                  funds from the MDE and then disburse to their employees. The final step of the MDE
                  approval process is not set to end until the Middle of June. Money cannot be
                  disbursed prior to this step being completed by the MDE.
                </p>
                <p>
                  Note: The disbursement process at the MDE level was delayed multiple times
                  throughout Cohort 1.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'What happens if I am receiving payments from the Student Loan Repayment Program, but I have a leave of absence that I have to take?'
                }
              >
                <p>
                  To determine if the leave is a type of leave that is not considered an
                  interruption to employment, please work with your HR department. A policy should
                  be in place to determine if there is an interruption to employment versus an
                  eligible leave of absence. Interruption to employment may impact eligibility,
                  pending the situation.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'I have previously applied for the Income Driven Repayment Plan but I have not been approved. If I receive approval prior to applying for the grant, would I qualify?'
                }
              >
                <p>
                  All eligibility requirements must be true at the time of the application, and you
                  must be able to provide the required proof documentation. If any requirement is
                  not true at the time of the application and/or you are unable to produce the proof
                  documentation required, the MDE will deem you ineligible for the program.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'Are there examples of definitions of “working directly with pre-K to grade 12 students?”'
                }
              >
                <p>
                  Your employer is ultimately responsible for determining whether an employee's
                  position or work assignment has at least 50% of its assigned time spent in direct
                  contact with pre-K to grade 12 students. If you have questions regarding this
                  requirement, you must contact your district directly.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'I’m an employee of a Michigan public school and I spend at least 50% of my working time in direct contact with preK-12 learners. However, I’m not allowed to enroll in one of the federal forgiveness plans listed on the website since my school is not identified as a Qualifying Employer for Public Service Loan Forgiveness. Can I participate?'
                }
              >
                <p>
                  In order to participate you must meet all program requirements including PSLF
                  eligibility. If your employer is not listed on the site as a qualified employer,
                  you will not be able to participate in the program.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'Is a participant hired from a third-party company like EduStaff eligible for the program, if they work directly with students for at least 50% of their assigned time?'
                }
              >
                <p>
                  You must be considered full-time at an eligible employer and meet the requirements
                  for the Public Service Loan Forgiveness (PSLF) program. If you do not work
                  directly for a qualifying employer to be eligible for PSLF, you cannot receive
                  funds through the 27k program.
                </p>
                <p>
                  PSLF Employment requirements:
                  <ul>
                    <li>
                      U.S.-based government organizations at any level (federal, state, local, or
                      tribal) – this includes the U.S. military
                    </li>
                    <li>
                      Not-for-profit organizations that are tax-exempt under Section 501(c)(3) of
                      the Internal Revenue Code
                    </li>
                    <li>
                      Other not-for-profit organizations that devote a majority of their full-time
                      equivalent employees to providing{' '}
                      <a
                        href="https://studentaid.gov/manage-loans/forgiveness-cancellation/public-service/qualifying-public-services"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        certain qualifying public services
                      </a>
                    </li>
                  </ul>
                </p>
                <p>
                  If EduStaff or any other similar employer does not fit into any of the three
                  categories above, they are not an eligible PSLF employer.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'Would educators working with students ages 18-26, or ages 0-3, be eligible?'
                }
              >
                <p>
                  Per MDE’s policy, the age of the students is not recognized as a qualifying
                  factor. To be eligible, you must have at least 50% of their working time spent in
                  direct contact with students in a PreK-12 program.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'I’m a school employee who works an hourly rate, and I work at least 32 hours during the school year with 50% or more of my working time in direct contact with students. However, from June through August I do not work. Would I still be eligible?'
                }
              >
                <p>
                  Yes, summer break is not factored in the determination of eligibility. If you work
                  at a district on a year-round or balanced calendar, you should consult your
                  specific district to understand what they would approve to meet this requirement.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'I meet all of the requirements and I am currently working full-time, with over 50% of my working time in direct contact with students. However, later this school year, I will be reduced to part-time work in order to pursue another degree. Should I apply?'
                }
              >
                <p>
                  Yes, you should apply for funding. Once your status officially changes, you are
                  required to update your district and the MDE. At that time, you would no longer be
                  eligible to receive funding.
                </p>
              </FaqQuestion>
              <FaqQuestion
                question={
                  'What if a participant in the Student Loan Repayment Program leaves employment in the district, or their work hours are reduced to less than 32 hours per week, or if their assignments change so that they are working less than 50% of their time in direct '
                }
              >
                <p>
                  You must immediately inform MDE at{' '}
                  <a href="mailto:MDE-EdWorkforceGrants@Michigan.gov">
                    MDE-EdWorkforceGrants@Michigan.gov
                  </a>{' '}
                  and your subsequent funding will stop.
                </p>
              </FaqQuestion>
            </CardBody>
          </Card>
        </DashboardSection>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  todos: Object.values(state.todos.cache),
  needAssistanceObject: Object.values(state.needAssistance.cache),
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(withPermissions(ContactNeedAssistance));
